import React from 'react';

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

import SingleCardTemplate from '@/components/layouts/SingleCardTemplate';
import Button from '@/components/ui/buttons/Button';
import { pageTitles } from '@/constants/pageTitles';
import Contact from '@/features/contact/components/Contact';
import ContactSupportBlock from '@/features/contact/components/ContactSupportBlock';
import ForgotHelpItem from '@/features/forgot/components/ForgotHelpItem';
import ForgotPageIndex from '@/features/forgot/components/ForgotPageIndex';
import ForgotPasswordForm from '@/features/forgot/components/ForgotPasswordForm';
import useAuthentication from '@/hooks/common/useAuthentication.ts';

import styles from './ForgotPage.module.scss';

const titleIds = {
  registration: 'registration',
  forgotPassword: 'forgot_password',
  forgotEmail: 'forgot_email',
  other: 'other',
} as const satisfies Record<string, string>;

const ForgotPage: React.FC = () => {
  const { isLoggedIn } = useAuthentication();

  return (
    <SingleCardTemplate title={pageTitles.forgot}>
      <Helmet>
        <title>{pageTitles.forgot}</title>
      </Helmet>
      <div className={styles.wrapper}>
        <p className={styles.description}>
          現在の状況を選んで、対処法をご確認ください。
        </p>
        <div className={styles.pageIndexContainer}>
          <ForgotPageIndex
            title="アカウントをお持ちでない方"
            items={[
              {
                id: titleIds.registration,
                label: 'アカウントの新規登録',
              },
            ]}
          />
          <ForgotPageIndex
            title="アカウントをお持ちの方"
            items={[
              {
                id: titleIds.forgotPassword,
                label: 'パスワードを忘れた',
              },
              {
                id: titleIds.forgotEmail,
                label: 'メールアドレスを忘れた',
              },
              {
                id: titleIds.other,
                label: '上記に該当しない方',
              },
            ]}
          />
        </div>

        <hr />

        <ForgotHelpItem id={titleIds.registration} title="アカウントの新規登録">
          <p className={styles.paragraph}>
            以下よりアカウント登録を行ってください。
          </p>
          <Button
            tag={Link}
            to="/registration"
            color="primary"
            className={styles.registrationButton}
          >
            アカウントの新規登録
          </Button>
        </ForgotHelpItem>
        <hr />
        <ForgotHelpItem id={titleIds.forgotPassword} title="パスワードを忘れた">
          <p className={styles.paragraph}>
            登録済みのメールアドレスに、パスワードを再設定するためのメールを送信します。
          </p>
          <ForgotPasswordForm />
        </ForgotHelpItem>
        <hr />
        <ForgotHelpItem
          id={titleIds.forgotEmail}
          title="メールアドレスを忘れた"
        >
          <p className={styles.paragraph}>
            下記のフォームよりお問い合わせください。
          </p>
          <ContactSupportBlock />
        </ForgotHelpItem>
        <hr />
        <ForgotHelpItem id={titleIds.other} title="上記に該当しない方">
          <Contact />
        </ForgotHelpItem>
        <hr />
        <div className={styles.buttons}>
          <Button
            tag={Link}
            to={isLoggedIn ? '/mypage/profile' : '/login'}
            color="outline"
            className={styles.button}
          >
            {isLoggedIn ? 'マイページに戻る' : 'ログイン画面に戻る'}
          </Button>
        </div>
      </div>
    </SingleCardTemplate>
  );
};

export default ForgotPage;
